import { FC, ReactNode, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GTagEventData, gaPushEvent } from '@/utils/analytics';
import { Session } from '@/models/user';
import { AuthService } from '@/services/auth.service';
import { VISITOR } from '@/models/session';
import SessionContext from './SessionContext';
import { LLMService } from '@/services/llm.service';
import { getCurrentOrg } from '@/services/org.service';
import { useNavigate } from 'react-router-dom';

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session>(VISITOR);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [orgIndex, setOrgIndex] = useState(0);
  const pushUserEvent = (action: string, data: GTagEventData = {}): void => {
    gaPushEvent(action, session.email, data);
  };

  const refreshThreads = () => {
    setTimeout(() => {
      LLMService.getThreads().then((threads) => {
        setSession((prevSession) => ({
          ...prevSession,
          threads,
        }));
      });
    }, 2000);
  };

  const logout = async () => {
    let endSessionUrl: string | null = null;
    try {
      // Call AuthService.logout() which handles token revocation for SSO users
      endSessionUrl = await AuthService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // Always reset local state, even if logout request fails
      queryClient.clear();
      setSession(VISITOR);
      setOrgIndex(0);
    }

    // For SSO users, redirect to Zitadel's end_session to clear the IdP session
    // This prevents auto-login on next SSO attempt
    if (endSessionUrl) {
      // Redirect to Zitadel to complete logout
      window.location.href = endSessionUrl;
    } else {
      // For password auth or if SSO logout failed, just navigate to sign-in
      navigate('/sign-in');
    }
  };

  const refreshSession = async (): Promise<void> => {
    try {
      const user = await AuthService.refreshSession();

      if (user.organizations.length > 0) {
        const currentOrg = getCurrentOrg();
        const index = user.organizations.findIndex(
          (org) => org.id === currentOrg
        );

        if (index > -1) {
          setOrgIndex(index);
        } else {
          setOrgIndex(0);
        }
      }
      setSession(user);
    } catch (err) {
      console.error(err);
      logout();
    }
  };

  const value = {
    session,
    setSession,
    orgIndex,
    setOrgIndex,
    logout,
    refreshSession,
    isAuthenticated: session.roles.length > 0,
    refreshThreads,
    pushUserEvent,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
